import { useTranslation } from 'react-i18next';
import { initialsOf } from '../helpers';
import type { BookingPageTeamMember } from '../types';

export function Avatar({ name, size = 48 }: { name: string; size?: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-pill border-2 border-surface bg-brand-50 font-semibold text-brand-700"
      style={{ width: size, height: size, fontSize: Math.round(size / 3) }}
      aria-hidden="true"
    >
      {initialsOf(name)}
    </span>
  );
}

export function TeamSection({ team }: { team: BookingPageTeamMember[] }) {
  const { t } = useTranslation('bookingPage');
  return (
    <section className="flex flex-col gap-4" data-testid="team-section">
      <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink sm:text-[24px]">{t('team.title')}</h2>
      <div className="-mr-4 flex gap-3 overflow-x-auto pb-1 sm:mr-0 sm:grid sm:grid-cols-3 sm:overflow-visible">
        {team.map((member) => (
          <div key={member.id} className="flex w-[168px] shrink-0 flex-col gap-3 rounded-2xl border border-line bg-surface p-4 sm:w-auto sm:px-[22px] sm:py-5">
            <Avatar name={member.name} />
            {/* Name only: the specialization is still on the member record and
                shown in the single-person "About <name>" block, but in this
                list it added a second line per card for little gain. */}
            <span className="text-[15px] font-semibold text-ink">{member.name}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function AboutPerson({ member, about }: { member: BookingPageTeamMember; about: string | null }) {
  const { t } = useTranslation('bookingPage');
  const text = member.bio || about;
  return (
    <section className="flex flex-col gap-4" data-testid="about-person">
      <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink sm:text-[24px]">
        {t('team.about', { name: member.name.split(' ')[0] })}
      </h2>
      <div className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5 sm:px-[26px] sm:py-6">
        <div className="flex items-center gap-4">
          <Avatar name={member.name} size={64} />
          <div className="flex flex-col gap-1">
            <span className="font-display text-[18px] font-semibold tracking-tight text-ink">{member.name}</span>
            {member.specialization && <span className="text-body text-soft">{member.specialization}</span>}
          </div>
        </div>
        {text && <p className="whitespace-pre-line text-body-lg text-soft">{text}</p>}
      </div>
    </section>
  );
}

export function AboutSection({ about }: { about: string }) {
  const { t } = useTranslation('bookingPage');
  return (
    <section className="flex flex-col gap-3" data-testid="about-section">
      <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink sm:text-[24px]">{t('about.title')}</h2>
      <p className="max-w-[720px] whitespace-pre-line text-body-lg text-soft">{about}</p>
    </section>
  );
}
